import React, { useMemo, useState } from "react";
import {
  Download,
  Mail,
  RefreshCw,
  Search,
  ChevronDown,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { accentHex, formatRelative, STATUS_TONE } from "../lib/theme";
import { exportUrl, runAgent, updateLead } from "../lib/api";
import { toast } from "sonner";
import DraftEmailModal from "./DraftEmailModal";

const STATUSES = [
  "Not contacted",
  "Email sent",
  "Chaser sent",
  "Replied",
  "Call scheduled",
  "Interested",
  "Deal closed",
  "Not interested",
  "Unsubscribed",
];

export default function AgentPanel({ agent, leads, reloadLeads, reloadAgents, onEdit, onNewSubAgent, hasChildren }) {
  const [running, setRunning] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeLead, setActiveLead] = useState(null);
  const accent = accentHex(agent.accent_color);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "all" && l.outreach_status !== statusFilter) return false;
      if (!q) return true;
      return (
        l.name.toLowerCase().includes(q) ||
        (l.org || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.location || "").toLowerCase().includes(q) ||
        (l.notes || "").toLowerCase().includes(q)
      );
    });
  }, [leads, query, statusFilter]);

  const isParentWithChildren = Boolean(hasChildren);

  const onRun = async () => {
    if (isParentWithChildren) {
      toast.error("Select a sub-agent to run.");
      return;
    }
    setRunning(true);
    toast.message("Claude is searching the web…", {
      description: "Finding verified leads. This can take 30–90 seconds.",
    });
    try {
      const res = await runAgent(agent.id);
      if (res.new_count === 0) {
        toast.info("No new leads this run. Try again later.");
      } else {
        toast.success(`${res.new_count} new ${res.new_count === 1 ? "lead" : "leads"} found`);
      }
      await Promise.all([reloadLeads(), reloadAgents()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claude run failed");
    } finally {
      setRunning(false);
    }
  };

  const onStatusChange = async (leadId, value) => {
    try {
      await updateLead(leadId, { outreach_status: value });
      await reloadLeads();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-aot-bg" data-testid="agent-panel">
      {/* Agent header */}
      <header className="border-b border-aot-border px-8 py-6 relative aot-noise">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <span
                className="inline-block w-2 h-2"
                style={{ backgroundColor: accent }}
              />
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-aot-muted">
                {agent.group === "fundraising" ? "Fundraising" : "Project · Agent"}
              </span>
            </div>
            <h1
              className="font-display text-3xl md:text-4xl font-bold tracking-tight"
              data-testid="agent-title"
            >
              {agent.name}
            </h1>
            <div className="mt-3 flex items-center gap-5 text-xs font-mono text-aot-muted uppercase tracking-wider">
              <span>{agent.geography}</span>
              <span className="text-aot-subtle">·</span>
              <span>
                <span className="text-aot-text tabular-nums">{leads.length}</span> leads
              </span>
              <span className="text-aot-subtle">·</span>
              <span>Last run: {formatRelative(agent.last_run_at)}</span>
              <span className="text-aot-subtle">·</span>
              <span>Lang: {agent.language}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              data-testid="edit-agent-button"
              className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-aot-border text-aot-muted hover:text-aot-text hover:bg-aot-surface transition-colors flex items-center gap-2"
              title="Edit agent"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            {onNewSubAgent && (
              <button
                onClick={onNewSubAgent}
                data-testid="new-subagent-button"
                className="px-3 py-2 text-xs font-mono uppercase tracking-wider border border-aot-border text-aot-muted hover:text-aot-text hover:bg-aot-surface transition-colors flex items-center gap-2"
                title="Add sub-agent"
              >
                <Plus className="w-3.5 h-3.5" />
                Sub-agent
              </button>
            )}
            <a
              href={exportUrl(agent.id)}
              data-testid="export-csv-button"
              className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-aot-border text-aot-muted hover:text-aot-text hover:bg-aot-surface transition-colors flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </a>
            <button
              onClick={onRun}
              disabled={running || isParentWithChildren}
              data-testid="run-agent-button"
              className="px-5 py-2 text-xs font-mono uppercase tracking-wider bg-aot-text text-aot-bg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
            >
              {running ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              {running ? "Claude searching…" : "Run Now"}
            </button>
          </div>
        </div>

        {/* Brief */}
        <div className="mt-6 max-w-4xl text-sm text-aot-muted leading-relaxed border-l-2 pl-4 py-1" style={{ borderColor: accent }}>
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-aot-subtle block mb-1">
            Brief
          </span>
          {agent.brief}
        </div>

        {running && (
          <div className="aot-scan h-[2px] mt-6" />
        )}
      </header>

      {/* Toolbar */}
      <div className="px-8 py-4 border-b border-aot-border flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-aot-subtle" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, org, email, notes…"
            data-testid="leads-search-input"
            className="w-full bg-aot-surface border border-aot-border pl-9 pr-3 py-2 text-sm text-aot-text placeholder:text-aot-subtle focus:outline-none focus:border-aot-teal transition-colors"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-aot-subtle">
            Status
          </span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="h-9 min-w-[160px] bg-aot-surface border-aot-border rounded-none text-xs"
              data-testid="status-filter-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-aot-surface border-aot-border rounded-none">
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto text-[11px] font-mono text-aot-subtle uppercase tracking-wider">
          Showing <span className="text-aot-text tabular-nums">{filtered.length}</span> / {leads.length}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {leads.length === 0 ? (
          <EmptyState running={running} onRun={onRun} isParent={isParentWithChildren} />
        ) : (
          <table className="w-full text-sm border-collapse" data-testid="leads-table">
            <thead className="sticky top-0 bg-aot-bg z-10">
              <tr className="border-b border-aot-border">
                <Th>Name</Th>
                <Th>Organisation</Th>
                <Th>Email</Th>
                <Th>Location</Th>
                <Th className="text-right">Fit</Th>
                <Th>Notes</Th>
                <Th>Status</Th>
                <Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-aot-border/60 hover:bg-aot-surface/40 transition-colors aot-fade-in"
                  data-testid={`lead-row-${l.id}`}
                >
                  <td className="py-3 px-4 align-top">
                    <div className="text-aot-text font-medium">{l.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-aot-subtle mt-0.5">
                      {l.contact_route}
                    </div>
                  </td>
                  <td className="py-3 px-4 align-top text-aot-muted">{l.org || "—"}</td>
                  <td className="py-3 px-4 align-top">
                    <a
                      href={`mailto:${l.email}`}
                      className="text-aot-teal hover:underline font-mono text-xs inline-flex items-center gap-1"
                    >
                      {l.email}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="py-3 px-4 align-top text-aot-muted">{l.location || "—"}</td>
                  <td className="py-3 px-4 align-top text-right">
                    <ScoreBadge score={l.score} accent={accent} />
                  </td>
                  <td className="py-3 px-4 align-top text-aot-muted max-w-[360px]">
                    <div className="line-clamp-3 text-[13px]" title={l.notes}>
                      {l.notes || "—"}
                    </div>
                  </td>
                  <td className="py-3 px-4 align-top">
                    <Select
                      value={l.outreach_status}
                      onValueChange={(v) => onStatusChange(l.id, v)}
                    >
                      <SelectTrigger
                        className={[
                          "h-8 min-w-[140px] bg-transparent rounded-none text-xs border",
                          STATUS_TONE[l.outreach_status] || "text-aot-muted border-aot-border",
                        ].join(" ")}
                        data-testid={`lead-status-select-${l.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aot-surface border-aot-border rounded-none">
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-3 px-4 align-top text-right">
                    <button
                      onClick={() => setActiveLead(l)}
                      data-testid={`draft-email-button-${l.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-aot-border text-aot-muted hover:text-aot-text hover:border-aot-teal hover:bg-aot-surface text-xs font-mono uppercase tracking-wider transition-colors"
                    >
                      <Mail className="w-3 h-3" />
                      Draft
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-aot-subtle text-sm">
                    No leads match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {activeLead && (
        <DraftEmailModal
          lead={activeLead}
          agent={agent}
          onClose={() => setActiveLead(null)}
        />
      )}
    </main>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={
        "text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.25em] text-aot-muted font-medium border-b border-aot-border " +
        className
      }
    >
      {children}
    </th>
  );
}

function ScoreBadge({ score, accent }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  return (
    <div className="inline-flex flex-col items-end gap-1 min-w-[48px]">
      <span className="font-mono tabular-nums text-sm text-aot-text">{pct}</span>
      <div className="w-12 h-0.5 bg-aot-border">
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: accent }}
        />
      </div>
    </div>
  );
}

function EmptyState({ running, onRun, isParent }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 px-8 text-center">
      <div className="w-10 h-10 border border-aot-border flex items-center justify-center mb-5">
        <RefreshCw className={`w-4 h-4 text-aot-muted ${running ? "animate-spin" : ""}`} />
      </div>
      <p className="font-display text-xl text-aot-text mb-2">No leads yet</p>
      <p className="text-sm text-aot-muted max-w-sm mb-6">
        {isParent
          ? "Select a country sub-agent from the sidebar to start finding leads."
          : "Click Run Now to have Claude search the web and return verified, scored leads based on this agent's brief."}
      </p>
      {!isParent && (
        <button
          onClick={onRun}
          disabled={running}
          className="px-5 py-2.5 bg-aot-text text-aot-bg text-xs font-mono uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-40"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {running ? "Claude searching…" : "Run first search"}
        </button>
      )}
    </div>
  );
}
